import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Building2, Wifi, Activity, Shield, Video, Phone, CheckCircle2, Mail, PhoneCall, Globe as GlobeIcon } from 'lucide-react';

const CoverageArea = () => {
  const navigate = useNavigate();

  const offices = [
    {
      icon: <Building2 className="h-10 w-10" />,
      city: "New Delhi",
      role: "Central operations and corporate hub",
      color: "from-red-600 to-orange-600"
    },
    {
      icon: <Building2 className="h-10 w-10" />,
      city: "Haryana",
      role: "Regional service and network operations center",
      color: "from-yellow-500 to-orange-500"
    },
    {
      icon: <Building2 className="h-10 w-10" />,
      city: "Bihar",
      role: "Eastern region technical and support hub",
      color: "from-pink-500 to-red-500"
    },
    {
      icon: <Building2 className="h-10 w-10" />,
      city: "Uttar Pradesh",
      role: "Northern region infrastructure and field services",
      color: "from-purple-500 to-pink-500"
    }
  ];

  const services = [
    {
      icon: <Activity className="h-12 w-12" />,
      title: "Lease Line Internet",
      description: "Dedicated high-speed connection with guaranteed bandwidth",
      color: "border-indigo-400"
    },
    {
      icon: <Shield className="h-12 w-12" />,
      title: "Enterprise Networking Solutions",
      description: "Secure and scalable connectivity",
      color: "border-green-400"
    },
    {
      icon: <Video className="h-12 w-12" />,
      title: "CCTV & Security Systems",
      description: "Smart surveillance solutions",
      color: "border-blue-400"
    },
    {
      icon: <Phone className="h-12 w-12" />,
      title: "VOIP & Communication Services",
      description: "Seamless voice connectivity",
      color: "border-purple-400"
    }
  ];

  const coverageFeatures = [
    "Urban, suburban, and rural area coverage",
    "24/7 network monitoring and support",
    "Same day installation in most areas",
    "Dedicated field service teams",
    "Redundant infrastructure for reliability",
    "Expanding to new cities every month"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-cyan-600/20 to-indigo-600/20"></div>
        
        {/* Floating particles effect */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/30 rounded-full animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            ></div>
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-32 text-center">
          <div className="inline-block mb-6 px-6 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-500/30 rounded-full">
            <span className="text-blue-300 font-semibold">🌐 Pan India Network</span>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-black mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-400 animate-gradient">
            COVERAGE AREA
          </h1>
          
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
            Connecting Every <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Corner</span> of India
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
            Delivering <span className="text-blue-400 font-bold">high-speed internet</span>, secure networking, and advanced technology solutions across India with our <span className="text-cyan-400 font-bold">robust infrastructure</span>
          </p>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mt-16">
            <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm border border-blue-500/20 rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
              <div className="text-4xl font-black text-blue-400 mb-2">4+</div>
              <div className="text-gray-300 font-medium">Major Cities</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-500/10 to-indigo-500/10 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
              <div className="text-4xl font-black text-cyan-400 mb-2">24/7</div>
              <div className="text-gray-300 font-medium">Support</div>
            </div>
            <div className="bg-gradient-to-br from-indigo-500/10 to-blue-500/10 backdrop-blur-sm border border-indigo-500/20 rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
              <div className="text-4xl font-black text-indigo-400 mb-2">1000+</div>
              <div className="text-gray-300 font-medium">Customers</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm border border-blue-500/20 rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
              <div className="text-4xl font-black text-cyan-400 mb-2">99.9%</div>
              <div className="text-gray-300 font-medium">Uptime</div>
            </div>
          </div>
        </div>
      </div>

      {/* India Map Section with Office Locations - Modern Design */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-20">
            <div className="inline-block mb-4 px-6 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-500/30 rounded-full">
              <span className="text-blue-300 font-semibold">📍 Strategic Locations</span>
            </div>
            
            <h2 className="text-5xl md:text-7xl font-black mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-indigo-400">
                OUR NATIONAL
              </span>
              <br />
              <span className="text-white">PRESENCE</span>
            </h2>
            
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Strategically positioned across <span className="text-cyan-400 font-bold">4 major regions</span> to serve you better
            </p>
          </div>

          {/* Map Infographic */}
          <div className="grid lg:grid-cols-2 gap-12 items-center bg-gradient-to-br from-slate-800/50 to-purple-900/50 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-purple-500/20">
            {/* Left Side - India Map with Office Markers */}
            <div className="relative">
              <div className="relative bg-white p-6 rounded-2xl shadow-2xl border-4 border-purple-500/30">
                {/* India Map Image - Clean and visible */}
                <div className="relative w-full h-[600px] bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl overflow-hidden flex items-center justify-center">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/18/India_geographic_map.svg/800px-India_geographic_map.svg.png"
                    alt="India Map"
                    className="w-full h-full object-contain p-4"
                  />
                  
                  {/* Simple Dot Markers - Only visible dots */}
                  {/* New Delhi */}
                  <div className="absolute top-[28%] left-[48%] transform -translate-x-1/2 -translate-y-1/2">
                    <div className="relative">
                      <div className="absolute inset-0 w-6 h-6 bg-purple-500 rounded-full animate-ping opacity-60"></div>
                      <div className="relative w-6 h-6 bg-purple-600 rounded-full border-3 border-white shadow-lg"></div>
                    </div>
                  </div>

                  {/* Haryana */}
                  <div className="absolute top-[26%] left-[45%] transform -translate-x-1/2 -translate-y-1/2">
                    <div className="relative">
                      <div className="absolute inset-0 w-6 h-6 bg-blue-500 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.2s' }}></div>
                      <div className="relative w-6 h-6 bg-blue-600 rounded-full border-3 border-white shadow-lg"></div>
                    </div>
                  </div>

                  {/* Bihar */}
                  <div className="absolute top-[32%] left-[62%] transform -translate-x-1/2 -translate-y-1/2">
                    <div className="relative">
                      <div className="absolute inset-0 w-6 h-6 bg-cyan-500 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.4s' }}></div>
                      <div className="relative w-6 h-6 bg-cyan-600 rounded-full border-3 border-white shadow-lg"></div>
                    </div>
                  </div>

                  {/* Uttar Pradesh */}
                  <div className="absolute top-[30%] left-[52%] transform -translate-x-1/2 -translate-y-1/2">
                    <div className="relative">
                      <div className="absolute inset-0 w-6 h-6 bg-pink-500 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.6s' }}></div>
                      <div className="relative w-6 h-6 bg-pink-600 rounded-full border-3 border-white shadow-lg"></div>
                    </div>
                  </div>
                </div>
                
                {/* Map Caption */}
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-700 font-semibold flex items-center justify-center gap-2">
                    <MapPin className="h-5 w-5 text-red-500" />
                    Pan India Coverage - 4 Strategic Locations
                  </p>
                </div>
              </div>
            </div>

            {/* Right Side - Office Details with Arrows */}
            <div className="space-y-4">
              {/* New Delhi */}
              <div className="relative flex items-start gap-4 group hover:bg-gradient-to-r hover:from-purple-500/20 hover:to-transparent p-4 rounded-xl transition-all duration-300 border-l-4 border-purple-500 bg-gradient-to-r from-purple-500/5 to-transparent">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-purple-600 rounded-full flex items-center justify-center shadow-lg relative">
                    <span className="text-2xl font-bold text-white">1</span>
                    {/* Arrow pointing to map - Enhanced */}
                    <div className="absolute -left-24 top-1/2 transform -translate-y-1/2 hidden lg:block">
                      <svg width="90" height="6" className="overflow-visible">
                        <defs>
                          <marker id="arrowPurple" markerWidth="12" markerHeight="12" refX="10" refY="4" orient="auto" markerUnits="strokeWidth">
                            <path d="M0,0 L0,8 L10,4 z" fill="#9333ea" />
                          </marker>
                          <filter id="glow">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                            <feMerge>
                              <feMergeNode in="coloredBlur"/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                        </defs>
                        <line x1="0" y1="3" x2="75" y2="3" stroke="#9333ea" strokeWidth="4" markerEnd="url(#arrowPurple)" strokeDasharray="8,4" filter="url(#glow)" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-purple-400 mb-2">NEW DELHI</h3>
                  <p className="text-gray-300 leading-relaxed text-sm">
                    Central operations and corporate hub. Managing nationwide connectivity and strategic partnerships.
                  </p>
                </div>
              </div>

              {/* Haryana */}
              <div className="relative flex items-start gap-4 group hover:bg-gradient-to-r hover:from-blue-500/20 hover:to-transparent p-4 rounded-xl transition-all duration-300 border-l-4 border-blue-500 bg-gradient-to-r from-blue-500/5 to-transparent">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center shadow-lg relative">
                    <span className="text-2xl font-bold text-white">2</span>
                    {/* Arrow pointing to map - Enhanced */}
                    <div className="absolute -left-24 top-1/2 transform -translate-y-1/2 hidden lg:block">
                      <svg width="90" height="6" className="overflow-visible">
                        <defs>
                          <marker id="arrowBlue" markerWidth="12" markerHeight="12" refX="10" refY="4" orient="auto" markerUnits="strokeWidth">
                            <path d="M0,0 L0,8 L10,4 z" fill="#2563eb" />
                          </marker>
                          <filter id="glowBlue">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                            <feMerge>
                              <feMergeNode in="coloredBlur"/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                        </defs>
                        <line x1="0" y1="3" x2="75" y2="3" stroke="#2563eb" strokeWidth="4" markerEnd="url(#arrowBlue)" strokeDasharray="8,4" filter="url(#glowBlue)" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-blue-400 mb-2">HARYANA</h3>
                  <p className="text-gray-300 leading-relaxed text-sm">
                    Regional service and network operations center. 24/7 technical support and infrastructure management.
                  </p>
                </div>
              </div>

              {/* Bihar */}
              <div className="relative flex items-start gap-4 group hover:bg-gradient-to-r hover:from-cyan-500/20 hover:to-transparent p-4 rounded-xl transition-all duration-300 border-l-4 border-cyan-500 bg-gradient-to-r from-cyan-500/5 to-transparent">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-cyan-500 rounded-full flex items-center justify-center shadow-lg relative">
                    <span className="text-2xl font-bold text-white">3</span>
                    {/* Arrow pointing to map - Enhanced */}
                    <div className="absolute -left-24 top-1/2 transform -translate-y-1/2 hidden lg:block">
                      <svg width="90" height="6" className="overflow-visible">
                        <defs>
                          <marker id="arrowCyan" markerWidth="12" markerHeight="12" refX="10" refY="4" orient="auto" markerUnits="strokeWidth">
                            <path d="M0,0 L0,8 L10,4 z" fill="#06b6d4" />
                          </marker>
                          <filter id="glowCyan">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                            <feMerge>
                              <feMergeNode in="coloredBlur"/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                        </defs>
                        <line x1="0" y1="3" x2="75" y2="3" stroke="#06b6d4" strokeWidth="4" markerEnd="url(#arrowCyan)" strokeDasharray="8,4" filter="url(#glowCyan)" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-cyan-400 mb-2">BIHAR</h3>
                  <p className="text-gray-300 leading-relaxed text-sm">
                    Eastern region technical and support hub. Dedicated to expanding rural connectivity and customer service.
                  </p>
                </div>
              </div>

              {/* Uttar Pradesh */}
              <div className="relative flex items-start gap-4 group hover:bg-gradient-to-r hover:from-pink-500/20 hover:to-transparent p-4 rounded-xl transition-all duration-300 border-l-4 border-pink-500 bg-gradient-to-r from-pink-500/5 to-transparent">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-pink-600 rounded-full flex items-center justify-center shadow-lg relative">
                    <span className="text-2xl font-bold text-white">4</span>
                    {/* Arrow pointing to map - Enhanced */}
                    <div className="absolute -left-24 top-1/2 transform -translate-y-1/2 hidden lg:block">
                      <svg width="90" height="6" className="overflow-visible">
                        <defs>
                          <marker id="arrowPink" markerWidth="12" markerHeight="12" refX="10" refY="4" orient="auto" markerUnits="strokeWidth">
                            <path d="M0,0 L0,8 L10,4 z" fill="#ec4899" />
                          </marker>
                          <filter id="glowPink">
                            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                            <feMerge>
                              <feMergeNode in="coloredBlur"/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                        </defs>
                        <line x1="0" y1="3" x2="75" y2="3" stroke="#ec4899" strokeWidth="4" markerEnd="url(#arrowPink)" strokeDasharray="8,4" filter="url(#glowPink)" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-pink-400 mb-2">UTTAR PRADESH</h3>
                  <p className="text-gray-300 leading-relaxed text-sm">
                    Northern region infrastructure and field services. High-capacity backbone network deployment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Coverage Features - Modern Cards */}
          <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {coverageFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="group relative overflow-hidden bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-sm p-6 rounded-2xl border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/10 to-purple-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                <div className="relative flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-white font-semibold text-base">{feature}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Our Strategic Offices
            </h2>
            <p className="text-xl text-gray-700">Key operational hubs across India</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {offices.map((office, index) => (
              <Card key={index} className="border-4 border-yellow-300 hover:border-red-400 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto bg-gradient-to-br ${office.color} shadow-lg`}>
                    <div className="text-white">
                      {office.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">🏢 {office.city}</h3>
                  <p className="text-gray-600 leading-relaxed">{office.role}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-gradient-to-r from-red-100 to-pink-100 rounded-2xl p-8 border-4 border-pink-300">
            <p className="text-center text-lg text-gray-800 font-medium">
              <strong className="text-red-600">With our Pan India coverage,</strong> we continue to strengthen our network to reach urban, suburban, and rural areas, ensuring reliable service to every customer.
            </p>
          </div>
        </div>
      </section>

      {/* Services Offered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              ⚙️ What We Offer Across India
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Whether you are a home user, business enterprise, or government organization, Netix Fibernet guarantees the same quality and reliability — everywhere in India.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {services.map((service, index) => (
              <Card key={index} className={`border-4 ${service.color} hover:scale-105 transition-all duration-300 shadow-xl bg-white`}>
                <CardContent className="p-6 text-center">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 mx-auto"
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}>
                    <div className="text-white">
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Technology Images */}
          <div className="grid md:grid-cols-2 gap-8">
            <img
              src="https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljfGVufDB8fHx8MTc2MjI0MzExNXww&ixlib=rb-4.1.0&q=85"
              alt="Fiber Optic Technology by Netix Fibernet"
              className="rounded-2xl shadow-2xl border-4 border-cyan-300 w-full h-64 object-cover"
            />
            <img
              src="https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxmaWJlciUyMG9wdGljfGVufDB8fHx8MTc2MjI0MzExNXww&ixlib=rb-4.1.0&q=85"
              alt="Technology Solutions by Netix Fibernet"
              className="rounded-2xl shadow-2xl border-4 border-green-300 w-full h-64 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Infrastructure Section */}
      <section className="py-20 bg-gradient-to-br from-pink-50 to-red-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1760013767150-da8e4ded6286?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxuZXR3b3JrJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NjIyNDMxMjB8MA&ixlib=rb-4.1.0&q=85"
                alt="Network Infrastructure"
                className="rounded-2xl shadow-2xl border-4 border-pink-300"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold mb-6" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Robust Infrastructure
              </h2>
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                Our state-of-the-art network infrastructure is built to deliver consistent, high-speed connectivity across the nation. With redundant systems and advanced technology, we ensure 99.9% uptime for all our customers.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Advanced fiber optic network backbone</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Multiple redundant paths for reliability</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Real-time network monitoring 24/7</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Rapid deployment capabilities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 via-yellow-500 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)'
          }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            📞 Stay Connected
          </h2>
          <p className="text-2xl text-white mb-10">
            For coverage details or service availability in your area
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border-4 border-white">
              <Mail className="h-12 w-12 text-white mx-auto mb-4" />
              <h3 className="text-white font-bold text-xl mb-2">Email Us</h3>
              <a href="mailto:support@netixfibernet.com" className="text-white hover:underline text-lg">
                support@netixfibernet.com
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border-4 border-white">
              <PhoneCall className="h-12 w-12 text-white mx-auto mb-4" />
              <h3 className="text-white font-bold text-xl mb-2">Call Us</h3>
              <a href="tel:+919319919988" data-testid="coverage-support-mobile-link" className="text-white hover:underline text-lg block">
                +91 93199 19988
              </a>
              <a href="tel:06276459015" data-testid="coverage-office-phone-link" className="text-white hover:underline text-lg block">
                Office Phone - 06276 - 459015
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border-4 border-white">
              <GlobeIcon className="h-12 w-12 text-white mx-auto mb-4" />
              <h3 className="text-white font-bold text-xl mb-2">Visit Website</h3>
              <a href="https://www.netixfibernet.com" target="_blank" rel="noopener noreferrer" className="text-white hover:underline text-lg">
                www.netixfibernet.com
              </a>
            </div>
          </div>

          <button
            onClick={() => navigate('/signup')}
            className="h-16 px-10 text-xl font-bold bg-white text-red-600 hover:bg-gray-100 shadow-2xl rounded-lg hover:scale-105 transition-transform"
          >
            <MapPin className="inline h-6 w-6 mr-2" />
            Check Availability in Your Area
          </button>
        </div>
      </section>
    </div>
  );
};

export default CoverageArea;
